import {UrlObject} from "url";

import NextLink, {LinkProps} from "next/link";
import {useTranslation} from "ni18n";
import React, {useState} from "react";

import {useReadableSlotTime} from "../../../hooks/useReadableSlotTime";
import {useTypedSelector} from "../../../store/index";
import {RootStateLocation} from "../../../store/types";
import {isCI} from "../../_common/_constants";
import Glimmer from "../Glimmer";
import ImagePair, {shouldRenderImages} from "./ImagePair";

type Props = {
  location: RootStateLocation;
  href?: string | LinkProps["href"];
  onClick?: () => void;
  soonestSlotTime?: number;
  clampLocName?: boolean;
  left?: React.ReactElement;
  loading?: boolean;
  images?: {src: string; alt?: string}[];
};

const Loading = React.memo(function Loading({images}: {images: Props["images"]}) {
  return (
    <div className="dg gg1 gafr w100p pv2">
      <ImagePair images={images} loading />
      <Glimmer height={30} style={{marginTop: "0.5rem"}} />
      <Glimmer height={22} width="70%" />
      <Glimmer height={22} width="30%" />
    </div>
  );
});

const Link = ({
  href,
  children,
  ...props
}: {
  href: string | undefined | UrlObject;
  children: JSX.Element | JSX.Element[];
} & Omit<React.HTMLAttributes<HTMLAnchorElement>, "children">) => {
  const shouldRendorPlainAnchor = typeof href === "string" || !href;
  return shouldRendorPlainAnchor ? (
    <a href={href} {...props}>
      {children}
    </a>
  ) : (
    <NextLink href={href} {...props}>
      {children}
    </NextLink>
  );
};

const ClinicListRow = React.forwardRef<HTMLLIElement, Props>(function _ClinicListRow(
  {location, href, onClick, soonestSlotTime, clampLocName, left, loading, images},
  ref,
) {
  const [hovered, setHovered] = useState(false);

  const {
    name,
    dist,
    address: {firstLine},
  } = location;
  const i18n = useTranslation();
  const {GROWTH_DISCOVERY_APPT_REASON_PAGE_AVAILABILITY_ENABLED} = useTypedSelector(
    state => state.featureFlags,
  );

  const readableSlotTime = useReadableSlotTime(soonestSlotTime);

  const disabled = !soonestSlotTime;
  const isNextLink = typeof href !== "string" && href?.pathname;

  return loading ? (
    <div className="p6 p4-sm">
      <Loading images={images} />
    </div>
  ) : (
    <li
      ref={ref}
      className="minh18"
      data-cy="get-care-location-link"
      style={{opacity: disabled ? 0.6 : 1}}
    >
      <Link
        href={href}
        {...(!isCI &&
          !isNextLink && {
            target: "_blank",
            rel: "noopener noreferrer",
          })}
        className={`df fdc p6 p4-sm br5 br0-xs t-100 ${disabled ? "cd" : "hover-bg-gray100"}`}
        onClick={onClick}
        onMouseEnter={() => !disabled && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-disabled={disabled}
      >
        <ImagePair images={images} className={shouldRenderImages(images) ? "pb3" : ""} />
        <div className="df jcsb">
          <div className="df aic">
            {left}
            <div className="df fdc oh tofe gray600 font-im fs14">
              <>
                <span
                  className={`fs22 fs18-lg gray800 font-csb pb1 oh tofe ${
                    clampLocName ? "lc1-lg wsnw-lg lcn-sm wsn-sm" : ""
                  }`}
                >
                  {name}
                </span>
                {Boolean(firstLine || dist) && (
                  <span className={`df aic fw pb3 t-100 ${hovered ? "gray800" : ""}`}>
                    <span className="wsnw">{firstLine}</span>
                    {/* @ts-expect-error TS2532: Object is possibly 'undefined'. */}
                    {dist > -1 && (
                      <span className="mh1" aria-hidden>
                        ·
                      </span>
                    )}
                    {/* @ts-expect-error TS2532: Object is possibly 'undefined'. */}
                    {dist > -1 && (
                      <span className="wsnw">{i18n.t("{{number}} mi", {number: dist})}</span>
                    )}
                  </span>
                )}
                {GROWTH_DISCOVERY_APPT_REASON_PAGE_AVAILABILITY_ENABLED && (
                  <strong
                    className={`font-ib wsnw fsn pb1 t-100 ${
                      !soonestSlotTime ? "gray600" : hovered ? "darkerGreen" : "darkGreen"
                    }`}
                  >
                    {soonestSlotTime ? (
                      <>
                        <span className="visually-hidden">{i18n.t("Soonest appointment:")}</span>
                        {i18n.t("Next:")} {readableSlotTime}
                      </>
                    ) : (
                      i18n.t("No availability")
                    )}
                  </strong>
                )}
              </>
            </div>
          </div>

          {soonestSlotTime && (
            <div className="df fdc aife jcc pl1">
              <span
                className={`cIcon-arrRight fs24 fs20-sm gray400 t-100 ${hovered ? "gray600" : ""}`}
                aria-hidden
              />
            </div>
          )}
        </div>
        <div className="visually-hidden">{i18n.t("(opens in new tab)")}</div>
      </Link>
    </li>
  );
});

export default ClinicListRow;
